// @ts-nocheck
import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Chip,
  CircularProgress,
  Typography,
  Tooltip,
} from "@mui/material";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch, useSelector } from "react-redux";
import { switchActiveRoleAction } from "../../../store/actions/auth";
import { ROLE_DISPLAY_NAMES } from "../../../staticData/userRoles";

/**
 * RoleSwitcher — dropdown in the header to switch `activeRole` when the user
 * has more than one approved role. Appears only if `approvedRoles.length > 1`.
 *
 * Click on an option:
 *   1. dispatch switchActiveRoleAction(role)
 *   2. backend validates (role ∈ approvedRoles) + issues a new JWT
 *   3. reducer updates activeRole + tokens
 *   4. action forces window.location.replace('/') to rebuild
 *      layouts/routes with the new activeRole
 *
 * Visual: clickable chip for the current role + dropdown listing all
 * approvedRoles (including the current one, marked). Spinner while switching.
 */
const RoleSwitcher = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const activeRole = auth?.activeRole || auth?.userRole;
  const approvedRoles = Array.isArray(auth?.approvedRoles)
    ? auth.approvedRoles
    : [];
  const switching = !!auth?.switchingRole;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Only render if there are 2+ approved roles
  if (approvedRoles.length < 2) return null;

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSwitch = (role) => {
    handleClose();
    if (role === activeRole) return;
    dispatch(switchActiveRoleAction(role));
  };

  const displayActive =
    ROLE_DISPLAY_NAMES[activeRole] || activeRole || "—";

  return (
    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
      <Tooltip title="Switch interface" arrow placement="bottom">
        <Chip
          icon={
            switching ? (
              <CircularProgress size={16} sx={{ color: "#00EEC5" }} />
            ) : (
              <SwitchAccountIcon sx={{ color: "#00EEC5 !important" }} />
            )
          }
          deleteIcon={<KeyboardArrowDownIcon />}
          onDelete={handleClick}
          onClick={handleClick}
          label={displayActive}
          variant="outlined"
          sx={{
            cursor: "pointer",
            color: "#fff",
            borderColor: "#00EEC5",
            "& .MuiChip-deleteIcon": {
              color: "#00EEC5",
            },
            "&:hover": {
              backgroundColor: "rgba(0, 238, 197, 0.08)",
            },
          }}
          disabled={switching}
        />
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            backgroundColor: "#1a1a1a",
            color: "#fff",
            minWidth: 220,
            border: "1px solid #00EEC5",
            borderRadius: "8px",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #333" }}>
          <Typography
            variant="caption"
            sx={{ color: "#999", textTransform: "uppercase", letterSpacing: 1 }}
          >
            Switch interface
          </Typography>
        </Box>
        {approvedRoles.map((role) => {
          const isActive = role === activeRole;
          return (
            <MenuItem
              key={role}
              selected={isActive}
              onClick={() => handleSwitch(role)}
              sx={{
                py: 1.2,
                px: 2,
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 238, 197, 0.15) !important",
                },
                "&:hover": {
                  backgroundColor: "rgba(0, 238, 197, 0.08)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography sx={{ color: "#fff" }}>
                  {ROLE_DISPLAY_NAMES[role] || role}
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#00EEC5", fontWeight: 600 }}
                  >
                    ACTIVE
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default RoleSwitcher;
